export type PublicEmploymentType =
  | 'full-time'
  | 'part-time'
  | 'contract'
  | 'internship'
  | 'temporary';

export type PublicWorkMode = 'remote' | 'onsite' | 'hybrid';
export type PublicExperienceLevel = 'entry' | 'mid' | 'senior' | 'executive';

function looksLikeRequisitionCode(value: string): boolean {
  return /^\d[\dA-Za-z]*$/.test(value)
    && (value.match(/\d/g)?.length ?? 0) >= 3;
}

/**
 * Repair punctuation that was decoded through the wrong Windows-1252/UTF-8
 * boundary. This is deliberately punctuation-only: attempting to re-decode the
 * whole string can corrupt correctly encoded Indian names and job content.
 */
function normalizePublicPunctuation(value: string): string {
  return value
    .replace(/â€¢/g, '•')
    .replace(/â€“/g, '–')
    .replace(/â€”/g, '—')
    .replace(/â€˜/g, '‘')
    .replace(/â€™/g, '’')
    .replace(/â€œ/g, '“')
    .replace(/â€�/g, '”')
    .replace(/Â(?=\s|•|–|—|$)/g, '');
}

/**
 * Conservative candidate-facing title cleanup.
 *
 * Recruiter source data remains untouched. We remove only digit-leading ATS
 * requisition prefixes that are followed by an explicit separator, repair the
 * known replacement-character punctuation shape, expand underscores, and
 * collapse whitespace. A title containing only a code is deliberately kept so
 * the publication eligibility layer can route it to review rather than
 * silently inventing a role name.
 */
export function normalizePublicJobTitle(value: unknown): string {
  const original = typeof value === 'string' ? value.trim() : '';
  if (!original) return '';

  let title = normalizePublicPunctuation(original)
    .replace(/[\uFFFD]/g, ' – ')
    .replace(/\s+\?\s+/g, ' – ')
    .replace(/_+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const first = title.match(/^(\d[\dA-Za-z]*)([\s\S]*)$/);
  if (first && looksLikeRequisitionCode(first[1])) {
    let rest = first[2];

    // Consume additional comma/ampersand/slash-separated requisition codes.
    for (;;) {
      const next = rest.match(/^\s*[,/&]\s*(\d[\dA-Za-z]*)([\s\S]*)$/);
      if (!next || !looksLikeRequisitionCode(next[1])) break;
      rest = next[2];
    }

    const separated = rest.match(/^\s*(?:\|\||[-–—:|_,])\s*(\S[\s\S]*)$/);
    if (separated) title = separated[1];
  }

  title = title
    .replace(/\s*,\s*/g, ', ')
    .replace(/\s*(?:\|\||[-–—:,|])\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim();
  return title || original;
}

/**
 * Candidate-facing long-form job text normalization.
 *
 * ATS/Word imports commonly lose two distinct characters as a literal `?`:
 * a line-leading bullet and a range dash (`5?8 years`). Body text therefore
 * has its own contextual rules and must never reuse title normalization, where
 * a separated `?` represents a title divider instead of a bullet.
 *
 * The function preserves HTML structure and introduces no markup; callers must
 * still apply their normal HTML sanitizer after this content repair.
 */
export function normalizePublicJobDescription(value: unknown): string {
  const original = typeof value === 'string' ? value : '';
  if (!original) return '';

  return normalizePublicPunctuation(original)
    // A semantic <li> already owns its marker. Strip the damaged/imported
    // glyph there so the browser does not render a double bullet.
    .replace(
      /(<li\b[^>]*>)((?:\s*<(?:span|strong|em|b|i)\b[^>]*>)*)\s*(?:\?|\uFFFD|•)\s+/gi,
      '$1$2',
    )
    // Known replacement characters in these two positions have the same
    // semantics as their literal-question-mark counterparts below.
    .replace(/(\d)\s*\uFFFD\s*(\d)/g, '$1–$2')
    .replace(
      /(^|<(?:p|br)\b[^>]*>|\n)((?:\s*<(?:span|strong|em|b|i)\b[^>]*>)*)\s*\uFFFD\s+/gi,
      '$1$2• ',
    )
    // Preserve real questions in prose; only repair the source shapes that are
    // unambiguous: digit ranges and a marker at the start of a rendered line.
    .replace(/(\d)\s*\?\s*(\d)/g, '$1–$2')
    .replace(
      /(^|<(?:p|br)\b[^>]*>|\n)((?:\s*<(?:span|strong|em|b|i)\b[^>]*>)*)\s*\?\s+/gi,
      '$1$2• ',
    )
    // A spaced question mark inside a line is the other common lost-dash
    // shape ("Location ? Bangalore"). Normal prose does not put whitespace
    // before question punctuation, so genuine questions remain unchanged.
    .replace(/\s+\?\s+/g, ' – ');
}

/**
 * Normalize one structured responsibility/requirement/benefit item. The UI
 * supplies its own list marker, so a damaged or real leading bullet is removed
 * after punctuation repair instead of rendered twice.
 */
export function normalizePublicJobBullet(value: unknown): string {
  const normalized = normalizePublicJobDescription(value).trim();
  return normalized
    .replace(/^(?:\?|\uFFFD|[•·▪◦‣⁃]|[-–—])\s+/, '')
    .trim();
}

function compact(value: unknown): string {
  return typeof value === 'string'
    ? value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '')
    : '';
}

/** Normalize legacy ATS employment values without inventing an answer. */
export function normalizePublicEmploymentType(value: unknown): PublicEmploymentType | null {
  const token = compact(value);
  if (['fulltime', 'clientfulltime', 'permanent', 'regular'].includes(token)) return 'full-time';
  if (['parttime'].includes(token)) return 'part-time';
  if (['contract', 'contractor', 'freelance', 'consultant'].includes(token)) return 'contract';
  if (['intern', 'internship', 'trainee'].includes(token)) return 'internship';
  if (['temporary', 'temp'].includes(token)) return 'temporary';
  return null;
}

/** Normalize work arrangement; ambiguous values remain absent. */
export function normalizePublicWorkMode(value: unknown, remoteOk?: unknown): PublicWorkMode | null {
  const token = compact(value);
  if (token === 'remote' || token === 'workfromhome' || token === 'wfh') return 'remote';
  if (token === 'hybrid' || token === 'flexiblehybrid') return 'hybrid';
  if (['onsite', 'inoffice', 'office', 'workfromoffice', 'wfo'].includes(token)) return 'onsite';
  if (remoteOk === true || remoteOk === 1 || remoteOk === '1' || remoteOk === 'true') return 'remote';
  return null;
}

/** Normalize broad experience bands used by public filters and labels. */
export function normalizePublicExperienceLevel(value: unknown): PublicExperienceLevel | null {
  const token = compact(value);
  if (['entry', 'entrylevel', 'junior', 'fresher', 'graduate'].includes(token)) return 'entry';
  if (['mid', 'midlevel', 'intermediate', 'experienced'].includes(token)) return 'mid';
  if (['senior', 'seniorlevel', 'lead', 'principal'].includes(token)) return 'senior';
  if (['executive', 'director', 'vp', 'vicepresident', 'cxo'].includes(token)) return 'executive';
  return null;
}

export function publicEmploymentTypeLabel(value: PublicEmploymentType | null | undefined): string | null {
  const labels: Record<PublicEmploymentType, string> = {
    'full-time': 'Full-time',
    'part-time': 'Part-time',
    contract: 'Contract',
    internship: 'Internship',
    temporary: 'Temporary',
  };
  return value ? labels[value] : null;
}

export function publicWorkModeLabel(value: PublicWorkMode | null | undefined): string | null {
  if (value === 'onsite') return 'On-site';
  if (value === 'remote') return 'Remote';
  if (value === 'hybrid') return 'Hybrid';
  return null;
}

export function publicExperienceLevelLabel(value: PublicExperienceLevel | null | undefined): string | null {
  const labels: Record<PublicExperienceLevel, string> = {
    entry: 'Entry level',
    mid: 'Mid level',
    senior: 'Senior level',
    executive: 'Executive',
  };
  return value ? labels[value] : null;
}
